import { DateRangeSelector } from "@/components/form-control/DateRangeSelector";
import { MapToolBox } from "@/components/map-tool-box";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { curbZoneToolboxIcons } from "./map-tool-box/map-tool-box-entry.const";
import { curbAreaMode, curbZoneCursorCell, parkingLotMode, workAreaMode } from "./curb-zone-map.const";
import { CurbZoneMapMode, CurbZonePageModeType } from "./curb-zone.type";
import { PickingInfo } from "deck.gl";
import { formatDate } from "@/helper/date-time";
import { numberFormat } from "@/helper/format-data";
import { useSetQueryParams } from "@/hooks/use-set-query-params";
import { Range } from "react-date-range";
import { ViewState } from "react-map-gl/mapbox";
import { GetCursor, ViewportPlaceType } from "types";
import {
  CurbZoneDetail,
  CurbZoneMultiDetail,
  CurbZoneTooltip,
  MapStyle,
  SearchPlace,
  SelectMapType,
  StreetView,
  SwitchModeView,
  UserReportListing,
} from "./components";
import { ModeViewType } from "constants/map.const";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CurbAreaDetail } from "./components/curb-area/curb-area-detail";
import { CurbZoneMultiplePrompt } from "./modes/add-curb-zone/curb-zone-multiple-prompt";
import { CurbZonePropertiesPrompt } from "./modes/add-curb-zone/curb-zone-properties-prompt";
import { EditorModeList } from "./components/editor-mode-list/editor-mode-list";
import { Label } from "@/components/ui/label";
import { ModifyCurbAreaPrompt } from "./components/curb-area/modify-curb-area-prompt";
import { ModifyParkingLotPrompt } from "./components/parking-lot/modify-parking-lot-prompt";
import { ModifyWorkAreaPrompt } from "./components/work-area/modify-work-area-prompt";
import { ParkingLotDetail } from "./components/parking-lot/parking-lot-detail";
import { SearchFilterCurbZone } from "components/search-and-filter";
import { WorkAreaDetail } from "./components/work-area/work-area-detail";
import { useCurbArea } from "./hooks/use-curb-area";
import { useCurbZone } from "./hooks/use-curb-zone";
import { useParkingLot } from "./hooks/use-parking-lot";
import { useUserReport } from "./hooks/use-user-report";
import { useWorkArea } from "./hooks/use-work-area";

export const CurbZoneMap = () => {
  const [appMode, setAppMode] = useState<CurbZoneMapMode>(CurbZonePageModeType.View);

  const setQueryParams = useSetQueryParams();
  const isCurbZone = useMemo(() => {
    return !curbAreaMode.includes(appMode) && !parkingLotMode.includes(appMode);
  }, [appMode]);
  const {
    markerPosition,
    viewport,
    modeView,
    setModeView,
    selectAddress,
    mapType,
    setMapType,
    handleDragEnd,
    handleSelectCurbZone,
    handleZooming,
    hoverInfo,
    handleCurbZoneHover,
    onViewStateChange,
    openCurbZoneModal,
    addPropertiesAndSave,
    currentLineParkingList,
    curbZoneDetail,
    removeSelectedFeatures,
    currentCity,
    showCurbZoneDetail,
    openParkingModal,
    handleDeleteCurbZone,
    showCurbZoneMultiDetail,
    cancelEditMultiple,
    setOpenCurbZoneMultipleModal,
    openCurbZoneMultipleModal,
    handleEditMultipleCurbZone,
    preZooming,
    handleBulkDeleteCurbZones,
    isSpaceBarPressed,
    filter,
    changeFilter,
    curbZoneLayers,
    handleCopyCurbZone,
    isCopyMode,
    editableLayer,
    alterMode,
    setAlterMode,
    currentIndexes,
    selectedDate,
    setSelectedDate,
    totalCalculatedSpaces,
  } = useCurbZone({ appMode, isCurbZone });

  const {
    handleSelectUserReport,
    selectedUserReport,
    userReportList,
    isLoadingUserReports,
    wrapMapRef,
    userReportLayer,
    setUserReportParams,
    userReportParams,
    isOpenUserReport,
    setIsOpenUserReport,
  } = useUserReport();

  const {
    curbAreaEditionData,
    setCurbAreaEditionData,
    curbAreaDetail,
    setCurbAreaDetail,
    showCurbAreaDetail,
    openCurbAreaModal,
    setOpenCurbAreaModal,
    curbAreaLayers,
    cancelCurbAreaChange,
    handleSelectCurbArea,
    saveCurbAreaProperties,
  } = useCurbArea({ appMode });

  const {
    parkingLotLayers,
    openParkingLotModal,
    parkingLotDetail,
    showParkingLotDetail,
    setParkingLotDetail,
    setOpenParkingLotModal,
    showWarning,
    resetEditParkingLotState,
    handleSelectParkingLot,
    saveParkingLotProperties,
  } = useParkingLot({
    appMode,
  });

  const {
    workAreaEditionData,
    setWorkAreaEditionData,
    workAreaDetail,
    setWorkAreaDetail,
    showWorkAreaDetail,
    openWorkAreaModal,
    setOpenWorkAreaModal,
    workAreaLayers,
    handleSelectWorkArea,
    saveWorkAreaProperties,
    resetDataOfEditState,
  } = useWorkArea({ appMode });

  const mergedLayers = [...curbAreaLayers, ...parkingLotLayers, ...workAreaLayers, ...curbZoneLayers, userReportLayer];

  const handleConfirmOk = () => resetEditParkingLotState();

  const [streetViewPosition, setStreetViewPosition] = useState<{ lat: number; lng: number }>({
    lat: viewport.latitude,
    lng: viewport.longitude,
  });
  const [newMarkerPosition, setNewMarkerPosition] = useState<ViewportPlaceType | null>(
    markerPosition ?? { latitude: viewport.latitude, longitude: viewport.longitude },
  );

  const [isStreetViewEnabled, setIsStreetViewEnabled] = useState<boolean>(true);
  const [isMarkerEnabled, setIsMarkerEnabled] = useState<boolean>(true);
  const [isStreetViewAndMarkerOptionsOpen, setIsStreetViewAndMarkerOptionsOpen] = useState<boolean>(false);

  const isUserInitiatedUpdate = useRef(false); // Flag to track manual updates

  const handleSelectLayer = (info: PickingInfo) => {
    if (curbAreaMode.includes(appMode)) {
      handleSelectCurbArea(info);
    } else if (parkingLotMode.includes(appMode)) {
      handleSelectParkingLot(info);
    } else if (workAreaMode.includes(appMode)) {
      handleSelectWorkArea(info);
    } else {
      handleSelectCurbZone(info);
    }

    if (info.coordinate) {
      const position = { lat: info.coordinate[1], lng: info.coordinate[0] };

      isUserInitiatedUpdate.current = true;
      setNewMarkerPosition({ latitude: position.lat, longitude: position.lng });
      setStreetViewPosition(position);
    }
  };

  const getCursor = useCallback(() => {
    if (curbZoneCursorCell.includes(appMode)) {
      return () => "cell";
    }
    if (appMode === CurbZonePageModeType.MultiEdit) {
      if (isSpaceBarPressed) {
        return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
      }
      return editableLayer.getCursor.bind(editableLayer) as GetCursor;
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  }, [isSpaceBarPressed, editableLayer, appMode]);

  //  update the marker when the street view position changes
  const onPositionChanged = (position: { lat: number; lng: number }) => {
    if (isUserInitiatedUpdate.current) {
      /*
      to prevent the marker from being updated a second time
      when the first marker update triggers a render that recalls
      this function to update the marker a second time
      */
      isUserInitiatedUpdate.current = false;
      return;
    }
    setNewMarkerPosition({ latitude: position.lat, longitude: position.lng });
  };

  useEffect(() => {
    if (currentCity?.position?.length === 2) {
      const [longitude, latitude] = currentCity.position;

      setNewMarkerPosition((prev) => {
        if (prev && prev.latitude === latitude && prev.longitude === longitude) return prev;
        return { latitude, longitude };
      });

      setStreetViewPosition((prev) => {
        if (prev.lat === latitude && prev.lng === longitude) return prev;
        return { lat: latitude, lng: longitude };
      });
    }
  }, [currentCity]);

  const showPanel = useMemo(() => {
    return isStreetViewEnabled || isOpenUserReport;
  }, [isStreetViewEnabled, isOpenUserReport]);

  const handleChangeDate = (item: Pick<Range, "startDate" | "endDate">) => {
    setSelectedDate(item);
    setQueryParams(
      {
        startDate: item.startDate ? formatDate(item.startDate.toDateString(), { format: "MM-dd-yyyy" }) : undefined,
        endDate: item.endDate ? formatDate(item.endDate.toDateString(), { format: "MM-dd-yyyy" }) : undefined,
      },
      { merge: true },
    );
  };

  return (
    <div className="h-full top-0 w-full absolute">
      <ResizablePanelGroup direction="vertical" autoSaveId="conditional" className="border">
        <ResizablePanel order={1} id="top">
          <div className="relative h-full" role="none" ref={wrapMapRef}>
            <div className="absolute top-16 left-6 z-10 flex gap-2">
              <SearchPlace onSelect={selectAddress} coordinates={viewport} />
              <SelectMapType mapType={mapType} onSelectMapType={(value) => setMapType(value)} />
              {currentCity && (
                <>
                  <SearchFilterCurbZone filter={filter} onChangeFilter={changeFilter} />
                  <DateRangeSelector
                    variant="secondary"
                    onChangeValue={handleChangeDate}
                    from={selectedDate.startDate}
                    to={selectedDate.endDate}
                  />
                </>
              )}
            </div>
            <div>
              <MapStyle
                markerPosition={isMarkerEnabled ? (markerPosition ?? newMarkerPosition) : null}
                mapType={mapType}
                modeView={modeView}
                layers={mergedLayers}
                handleDragEnd={handleDragEnd}
                handleSelectLayer={handleSelectLayer}
                handleZooming={(event) => handleZooming(event, preZooming.current)}
                onViewStateChange={({ viewState }) => onViewStateChange(viewState as unknown as ViewState)}
                viewport={viewport}
                getCursor={getCursor}
                onHover={handleCurbZoneHover}
              >
                {appMode === CurbZonePageModeType.View && hoverInfo?.properties && (
                  <CurbZoneTooltip left={hoverInfo.x} top={hoverInfo.y} curbZone={hoverInfo.properties} />
                )}
              </MapStyle>
            </div>

            {mapType === "Map box" && (
              <SwitchModeView modeView={modeView} selectModeView={(mode: ModeViewType) => setModeView(mode)} />
            )}
            {appMode === CurbZonePageModeType.MultiEdit && (
              <div className="absolute p-4 bottom-2 left-24 ml-6 bg-default rounded-md shadow-lg">
                <p>Press SPACE bar to move the map.</p>
                <p>Press SHIFT button to select one curb-zone.</p>
                <p>Press ALT button to select multiple curb-zones.</p>
              </div>
            )}
            {currentCity && (
              <div className="absolute top-30 left-8">
                <Label className="text-xs font-semibold font-sans">
                  Calculated spaces:<p className="font-medium">{numberFormat(totalCalculatedSpaces)}</p>
                </Label>
              </div>
            )}

            <div className="absolute top-36 left-8 z-5 gap-8">
              <MapToolBox appMode={appMode} setAppMode={setAppMode} toolboxIcons={curbZoneToolboxIcons} />
            </div>
            <div className="absolute top-36 left-36 z-5 gap-8">
              {currentIndexes.length > 0 && (
                <EditorModeList appMode={appMode} alterMode={alterMode} setAlterMode={setAlterMode} />
              )}
            </div>

            <div className="absolute bottom-10 right-8 z-5">
              <Popover open={isStreetViewAndMarkerOptionsOpen} onOpenChange={setIsStreetViewAndMarkerOptionsOpen}>
                <PopoverTrigger asChild>
                  <Button className="mr-2">Options StreetView & Marker</Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3" align="end" side="top">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="streetview"
                        checked={isStreetViewEnabled}
                        onCheckedChange={(checked) => {
                          setIsStreetViewEnabled(checked === true);
                          setIsStreetViewAndMarkerOptionsOpen(false);
                        }}
                      />
                      <Label htmlFor="streetview">Activer StreetView</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="marker"
                        checked={isMarkerEnabled}
                        onCheckedChange={(checked) => {
                          setIsMarkerEnabled(checked === true);
                          setIsStreetViewAndMarkerOptionsOpen(false);
                        }}
                      />
                      <Label htmlFor="marker">Afficher le marqueur</Label>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={() => setIsOpenUserReport()}>{isOpenUserReport ? "Hide" : "Show"} User Reports</Button>
            </div>

            {openCurbZoneModal && (
              <CurbZonePropertiesPrompt
                registerProperties={addPropertiesAndSave}
                prefill={(currentLineParkingList[0] || curbZoneDetail[0]) ?? undefined}
                id={currentLineParkingList[0]?.id as string}
                cancel={removeSelectedFeatures}
              />
            )}
            {currentCity
              ? showCurbZoneDetail && (
                  <CurbZoneDetail
                    cityId={currentCity.id}
                    curbZone={curbZoneDetail[0]?.properties}
                    resetState={removeSelectedFeatures}
                    openCurbZoneModal={openParkingModal}
                    handleRemoveCurbZone={handleDeleteCurbZone}
                    copyCurbZone={handleCopyCurbZone}
                    title={isCopyMode.current ? "Duplicated a Curb Zone" : null}
                  />
                )
              : null}
            {currentCity
              ? showCurbZoneMultiDetail && (
                  <CurbZoneMultiDetail
                    curbZones={curbZoneDetail}
                    resetState={cancelEditMultiple}
                    openCurbZoneMultiModal={setOpenCurbZoneMultipleModal}
                    handleRemoveMultiCurbZone={handleBulkDeleteCurbZones}
                  />
                )
              : null}

            {openCurbZoneMultipleModal && (
              <CurbZoneMultiplePrompt
                numberOfParking={curbZoneDetail?.length}
                cancel={cancelEditMultiple}
                handleEditMultipleCurbZone={handleEditMultipleCurbZone}
              />
            )}
            {openCurbAreaModal && (
              <ModifyCurbAreaPrompt
                appMode={appMode}
                saveCurbAreaProperties={saveCurbAreaProperties}
                setOpenCurbAreaModal={setOpenCurbAreaModal}
                curbAreaEditionData={curbAreaEditionData}
                setCurbAreaEditionData={setCurbAreaEditionData}
                curbAreaDetail={curbAreaDetail}
                cancelCurbAreaChange={cancelCurbAreaChange}
              />
            )}
            {openWorkAreaModal && (
              <ModifyWorkAreaPrompt
                appMode={appMode}
                saveWorkAreaProperties={saveWorkAreaProperties}
                setOpenWorkAreaModal={setOpenWorkAreaModal}
                workAreaEditionData={workAreaEditionData}
                setWorkAreaEditionData={setWorkAreaEditionData}
                workAreaDetail={workAreaDetail}
                cancelWorkAreaChange={resetDataOfEditState}
              />
            )}
            {workAreaDetail && showWorkAreaDetail && (
              <WorkAreaDetail
                workAreaDetail={workAreaDetail}
                setWorkAreaDetail={setWorkAreaDetail}
                setOpenWorkAreaModal={setOpenWorkAreaModal}
              />
            )}
            {curbAreaDetail && showCurbAreaDetail && (
              <CurbAreaDetail
                curbAreaDetail={curbAreaDetail}
                setCurbAreaDetail={setCurbAreaDetail}
                setOpenCurbAreaModal={setOpenCurbAreaModal}
              />
            )}
            {openParkingLotModal && (
              <ModifyParkingLotPrompt
                saveParkingLotProperties={saveParkingLotProperties}
                setOpenParkingLotModal={setOpenParkingLotModal}
                currentCity={currentCity}
                resetEditState={resetEditParkingLotState}
                parkingLotDetail={parkingLotDetail}
              />
            )}
            {parkingLotDetail && showParkingLotDetail && (
              <ParkingLotDetail
                parkingLotDetail={parkingLotDetail}
                setParkingLotDetail={setParkingLotDetail}
                setOpenParkingLotModal={setOpenParkingLotModal}
              />
            )}
            <AlertDialog open={showWarning}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Warning</AlertDialogTitle>
                  <AlertDialogDescription>Have not any curb zone</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogAction onClick={handleConfirmOk}>Close</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </ResizablePanel>

        {showPanel && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel order={2} id="bottom">
              {!isOpenUserReport && isStreetViewEnabled ? (
                <StreetView position={streetViewPosition} onPositionChanged={onPositionChanged} />
              ) : (
                <UserReportListing
                  isLoading={isLoadingUserReports}
                  userReportList={userReportList}
                  onSelectUserReport={handleSelectUserReport}
                  selectedUserReport={selectedUserReport}
                  userReportParams={userReportParams}
                  setUserReportParams={setUserReportParams}
                />
              )}
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
};
